/**
 * SoHeader (S2.3) — SO detail header card on the VendorDetailPage trio
 * pattern (ProductHeader precedent): back + breadcrumb (銷售 › 銷售訂單 ›
 * SO #), soNumber title + status Tag, external order number secondary (the
 * import dedupe key), pending-approval banner with the ROLE-GATED release
 * affordance (hidden — not disabled — for non-managers), meta row.
 *
 * Actions are affordances, not status noise (hide-don't-disable):
 * - 修改 Edit renders only while the modify flow is open (draft/confirmed).
 * - 確認 Confirm renders only on draft (server checklist is the gate).
 * - 建立 PO renders only when fulfillment is allowed (confirmed + price ok).
 */

import { Alert, Breadcrumb, Button, Card, Divider, Tag, theme } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EditOutlined,
  ShoppingCartOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TimestampPair } from '@/components/common/TimestampDisplay';
import { lineMarginComparable } from '@/lib/pricing';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import type { SalesOrder, SalesOrderPriceHoldReason } from '@/types/api/salesOrder';
import { SO_PRICE_DEVIATION_THRESHOLD_PCT } from '@/types/api/salesOrder';
import { formatAmount, formatMoney, formatPct, holdReasonKey } from '../components/soFormat';
import { SO_STATUS_TAG_COLOR } from './types';

interface SoHeaderProps {
  so: SalesOrder;
  /** Manager-gate result — the release button renders only when true. */
  canRelease: boolean;
  confirming: boolean;
  creatingPo: boolean;
  /** procurement.poFromSo flag + status/price gate resolved by the page. */
  showCreatePo: boolean;
  onConfirm: () => void;
  onRelease: () => void;
  onCreatePo: () => void;
}

export const SoHeader = ({
  so,
  canRelease,
  confirming,
  creatingPo,
  showCreatePo,
  onConfirm,
  onRelease,
  onCreatePo,
}: SoHeaderProps) => {
  const { t } = useTranslation(['orders', 'common']);
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const elevation = useElevation();

  const editable = so.status === 'draft' || so.status === 'confirmed';

  // The banner NAMES its reason(s) (2026-09-15): a margin hold must never read
  // as "deviates". The floor quoted is the one snapshotted on the header —
  // the number the hold was actually tested against.
  const holdReasonLabel = (reason: SalesOrderPriceHoldReason): string =>
    t(holdReasonKey(reason, true), {
      pct: SO_PRICE_DEVIATION_THRESHOLD_PCT,
      floor: formatPct(so.marginFloorPct),
    });

  const metaItem = (label: string, value: React.ReactNode) => (
    <div className="tw-flex tw-items-center tw-gap-2">
      <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>{label}</span>
      <span style={{ fontSize: token.fontSize, fontWeight: token.fontWeightStrong }}>{value}</span>
    </div>
  );

  const actionButtonStyle = {
    height: `${controlHeightTokens.controlHeightXL}px`,
    borderRadius: radiusTokens.card,
  };

  return (
    <Card
      className="tw-mb-6"
      style={{ ...elevation.card }}
    >
      {/* Back + breadcrumb */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/auth/sales-orders')}
          style={{ padding: 0, height: 'auto', lineHeight: 1 }}
        >
          {t('orders:so.detail.backToList')}
        </Button>
        <Breadcrumb
          items={[
            { title: t('common:menu.sales') },
            {
              title: (
                <a onClick={() => navigate('/auth/sales-orders')}>{t('orders:soListTitle')}</a>
              ),
            },
            { title: so.soNumber },
          ]}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Title + status Tag + actions */}
      <div className="tw-flex tw-justify-between tw-items-start tw-gap-6 tw-mb-4 tw-flex-wrap">
        <div className="tw-flex-1" style={{ minWidth: 0 }}>
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
            <h1
              style={{
                fontSize: token.fontSizeHeading3,
                fontWeight: token.fontWeightStrong,
                color: token.colorPrimary,
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              {so.soNumber}
            </h1>
            <Tag color={SO_STATUS_TAG_COLOR[so.status]} style={{ margin: 0 }}>
              {t(`orders:so.status.${so.status}`)}
            </Tag>
          </div>
          {so.externalOrderNumber && (
            <p
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorTextTertiary,
                margin: '4px 0 0 0',
              }}
            >
              {t('orders:so.fields.externalOrderNumber')}: {so.externalOrderNumber}
            </p>
          )}
        </div>

        <div className="tw-flex tw-gap-3 tw-flex-wrap">
          {editable && (
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/auth/sales-orders/${so.id}/edit`)}
              style={actionButtonStyle}
            >
              {t('orders:so.detail.modifyAction')}
            </Button>
          )}
          {showCreatePo && (
            <Button
              icon={<ShoppingCartOutlined />}
              loading={creatingPo}
              onClick={onCreatePo}
              style={actionButtonStyle}
            >
              {t('orders:createPo')}
            </Button>
          )}
          {so.status === 'draft' && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={confirming}
              onClick={onConfirm}
              style={actionButtonStyle}
            >
              {t('orders:so.detail.confirmAction')}
            </Button>
          )}
        </div>
      </div>

      {/* 價格審核 pending-approval banner w/ role-gated release */}
      {so.priceStatus === 'pending_approval' && (
        <Alert
          className="tw-mb-4"
          type="warning"
          showIcon
          message={t('orders:so.detail.pendingBanner.title')}
          description={
            <div className="tw-flex tw-flex-col tw-gap-1">
              {/* WHY first, then what is held (ux-lens plan §3 item 2). */}
              {so.priceHoldReasons.length > 0 && (
                <ul style={{ margin: 0, paddingInlineStart: token.paddingLG }}>
                  {so.priceHoldReasons.map((reason) => (
                    <li key={reason}>{holdReasonLabel(reason)}</li>
                  ))}
                </ul>
              )}
              <span>
                {t('orders:so.detail.pendingBanner.description')}
                {canRelease ? '' : ` ${t('orders:so.detail.pendingBanner.noReleasePermission')}`}
              </span>
            </div>
          }
          action={
            canRelease ? (
              <Button icon={<UnlockOutlined />} onClick={onRelease} style={actionButtonStyle}>
                {t('orders:so.detail.pendingBanner.releaseAction')}
              </Button>
            ) : undefined
          }
        />
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* Meta row */}
      <div className="tw-flex tw-gap-8 tw-flex-wrap tw-items-center" style={{ paddingTop: '8px' }}>
        {metaItem(
          t('orders:fields.customer'),
          <a
            onClick={() => navigate(`/auth/customers/${so.customerId}`)}
            style={{ color: token.colorPrimary, cursor: 'pointer' }}
          >
            {so.customerName}
          </a>
        )}
        {metaItem(t('orders:so.fields.orderDate'), so.orderDate || '-')}
        {metaItem(t('orders:so.fields.expectedShipDate'), so.expectedShipDate || '-')}
        {metaItem(t('orders:so.fields.deliveryDate'), so.deliveryDate || '-')}
        {metaItem(t('orders:fields.total'), formatMoney(so.currency, so.totalAmount))}
        {/* A total that would silently mix two currencies is not shown as a
            number — the lines say which ones cannot be read (2026-09-16). */}
        {so.totalEstMargin !== null &&
          so.lines.every((line) =>
            lineMarginComparable({
              landedCost: line.landedCost,
              currency: so.currency,
              priceTrace: line.priceTrace,
            })
          ) &&
          metaItem(
            t('orders:so.create.totalMargin'),
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                color: so.totalEstMargin >= 0 ? token.colorSuccess : token.colorError,
              }}
            >
              {formatAmount(so.totalEstMargin)}
            </span>
          )}
        <TimestampPair createdAt={so.createdAt} updatedAt={so.updatedAt} direction="horizontal" />
      </div>
    </Card>
  );
};

export default SoHeader;
